import DesignHarness from './components/pp/DesignHarness';
import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter as Router, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { getPaddle } from './utils/paddle';
import envConfig from './config/environment';
import CalendarSelector from './components/CalendarSelector';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import EventModal from './components/EventModal';
import NiceMobileCalendar from './components/NiceMobileCalendar';
import PatientsMobile from './components/PatientsMobile';
import Login from './components/Login';
import Register from './components/Register';
import Landing from './components/Landing';
import TermsPage from './components/TermsPage';
import PrivacyPage from './components/PrivacyPage';
import RefundPage from './components/RefundPage';
import ResetPassword from './components/ResetPassword';
import PublicConsentSign from './components/PublicConsentSign';
import PublicBooking from './components/PublicBooking';
import UpgradePrompt from './components/UpgradePrompt';
import AgendaBriefingWidget from './components/AgendaBriefingWidget';
import ExpirationsWidget from './components/ExpirationsWidget';
import { DialogProvider } from './components/AppDialog';
import OfflineBanner from './components/OfflineBanner';
import apiService, { authenticatedFetch } from './services/api';
import { Event } from './types/Event';
import logger from './utils/logger';
import { useChatNotifications } from './hooks/useChatNotifications';
import './i18n';

// wt-reputation (Onda 3C): public rating page loaded eagerly (tiny, no auth)
import PublicRating from './components/PublicRating';

// Lazy load heavy components
const LogViewer = lazy(() => import('./components/LogViewer'));
const IntakeFormBuilder = lazy(() => import('./components/IntakeFormBuilder')); // wt-intake (Onda 2B)
const PatientIntakeForm = lazy(() => import('./components/PatientIntakeForm')); // wt-intake (Onda 2B) public
const UnifiedConfirm = lazy(() => import('./components/UnifiedConfirm')); // unified pre-consultation (confirm + termo + anamnese), public
const ClientManagement = lazy(() => import('./components/ClientManagement'));
const ProcedureManagement = lazy(() => import('./components/ProcedureManagement'));
const MedicalRecordManagement = lazy(() => import('./components/MedicalRecordManagement'));
const Configuration = lazy(() => import('./components/Configuration'));
const RecallConfig = lazy(() => import('./components/RecallConfig'));
const BulkMessage = lazy(() => import('./components/BulkMessage'));
const RecallUpcomingModal = lazy(() => import('./components/RecallUpcomingModal'));
const MessageTemplates = lazy(() => import('./components/MessageTemplates'));
const AdminPanel = lazy(() => import('./components/AdminPanel'));
const SystemPanel = lazy(() => import('./components/SystemPanel'));
const BillingPage = lazy(() => import('./components/BillingPage'));
const GlobalFeedPage = lazy(() => import('./components/GlobalFeedPage'));
const PatientFeedMobile = lazy(() => import('./components/PatientFeed'));
const CandidateChat = lazy(() => import('./components/CandidateChat'));
const ConsentTemplates = lazy(() => import('./components/ConsentTemplates'));
const BookingConfig = lazy(() => import('./components/BookingConfig'));
const PatientPortal = lazy(() => import('./components/PatientPortal'));
// wt-financial (Onda 2C)
const FinancialDashboard = lazy(() => import('./components/FinancialDashboard'));
const InvoiceManager = lazy(() => import('./components/InvoiceManager'));
const CommissionReport = lazy(() => import('./components/CommissionReport'));
// wt-packages (Onda 3A)
const PackageManager = lazy(() => import('./components/PackageManager'));
// wt-analytics (Onda 3B)
const BusinessAnalytics = lazy(() => import('./components/BusinessAnalytics'));
const InventoryManagement = lazy(() => import('./components/InventoryManagement'));
// wt-reputation (Onda 3C)
const ReputationDashboard = lazy(() => import('./components/ReputationDashboard'));

// Kick off Paddle initialization early; result is cached in the module.
getPaddle();

// Apply persisted Sereno prefs (accent + theme) to <body class="sereno">
const _accent = localStorage.getItem('na_accent');
if (_accent && _accent !== 'sereno') document.body.setAttribute('data-accent', _accent);
const _theme = localStorage.getItem('na_theme');
if (_theme === 'dark') document.body.classList.add('dark');
if (_theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches) document.body.classList.add('dark');
const _scaleMap: Record<string, string> = { regular: '1', large: '1.125', xlarge: '1.25' };
const _scale = localStorage.getItem('na_ui_scale');
if (_scale && _scaleMap[_scale]) document.body.style.setProperty('--ui-scale', _scaleMap[_scale]);

interface Client {
  id: number;
  name: string;
  email?: string;
  phone?: string;
}

interface Procedure {
  id: number;
  name: string;
  durationMinutes: number;
  price?: number;
  color?: string;
}

function AppContent() {
  const { t } = useTranslation();
  const [events, setEvents] = useState<Event[]>([]);
  const lastAppointmentFetchRef = useRef<number>(0);
  const [clients, setClients] = useState<Client[]>([]);
  const [procedures, setProcedures] = useState<Procedure[]>([]);
  const [providers, setProviders] = useState<{ id: number; name: string; calendar_color: string | null; default_resource_id?: number | null }[]>([]);
  const [selectedProviderId, setSelectedProviderId] = useState<number | null>(null);
  const [resources, setResources] = useState<{ id: number; name: string; color: string }[]>([]);
  const [selectedResourceId, setSelectedResourceId] = useState<number | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [pendingEditClientId, setPendingEditClientId] = useState<number | null>(null);
  // deep link /pacientes/:id opens that patient's workspace on the clients view
  const [pendingFeedClientId, setPendingFeedClientId] = useState<number | null>(() => {
    const m = window.location.pathname.match(/^\/pacientes\/(\d+)/);
    return m ? parseInt(m[1], 10) : null;
  });
  const [pendingFeedTab] = useState<'photos' | 'forms' | 'profile' | undefined>(() => {
    const slug = (window.location.pathname.match(/^\/pacientes\/\d+\/([a-z]+)/) || [])[1];
    return ({ fotos: 'photos', fichas: 'forms', dados: 'profile' } as const)[slug as 'fotos'];
  });
  const [mobileClinicalPatientId, setMobileClinicalPatientId] = useState<number | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(() => typeof window !== 'undefined' ? window.innerWidth > 768 : false);
  const [currentView, setCurrentView] = useState(() => (/^\/pacientes\/\d+/.test(window.location.pathname) ? 'clients' : localStorage.getItem('na_currentView') || 'calendar'));
  useEffect(() => { localStorage.setItem('na_currentView', currentView); }, [currentView]);
  const location = useLocation();
  const navigate = useNavigate();
  const pathToAuth = (p: string): 'login' | 'register' | null =>
    p === '/login' ? 'login' : p === '/register' ? 'register' : null;
  const isPWA = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;
  const [showAuth, _setShowAuth] = useState<'login' | 'register' | null>(
    pathToAuth(location.pathname) ?? (isPWA ? 'login' : null)
  );
  const setShowAuth = (next: 'login' | 'register' | null) => {
    _setShowAuth(next);
    const target = next ? `/${next}` : '/';
    if (location.pathname !== target) navigate(target);
  };
  useEffect(() => {
    const next = pathToAuth(location.pathname);
    _setShowAuth(prev => (prev === next ? prev : next));
  }, [location.pathname]);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' ? window.innerWidth <= 768 : false);
  const { user, token, loading, logoutWithoutMessage, subscription, refreshSubscription, tenantSettings } = useAuth();
  const { unreadCount: unreadChatCount } = useChatNotifications(() => setCurrentView('candidate-messages'));
  const [booting, setBooting] = useState(true);
  const [clinicName, setClinicName] = useState<string>('');
  const [upcomingRecallOpen, setUpcomingRecallOpen] = useState(false);

  // Detect mobile device with improved detection
  useEffect(() => {
    // Minimal boot delay to satisfy tests expecting an initial Loading... state
    if (process.env.NODE_ENV === 'test') {
      setTimeout(() => setBooting(false), 0);
    } else {
      setBooting(false);
    }

    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    window.addEventListener('orientationchange', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
      window.removeEventListener('orientationchange', checkMobile);
    };
  }, []);

  // Handle sidebar state based on mobile detection
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    } else {
      setSidebarOpen(true);
    }
  }, [isMobile]);

  // Edge-swipe gesture: open sidebar by swiping from the left edge on mobile
  useEffect(() => {
    if (!isMobile) return;
    let startX = 0;
    let startY = 0;
    let tracking = false;

    const onTouchStart = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t) return;
      // Only track touches that start within 24px of the left edge
      if (t.clientX <= 24 && !sidebarOpen) {
        startX = t.clientX;
        startY = t.clientY;
        tracking = true;
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!tracking) return;
      const t = e.touches[0];
      if (!t) return;
      const dx = t.clientX - startX;
      const dy = Math.abs(t.clientY - startY);
      // Open when user has dragged at least 40px to the right AND not mostly vertical
      if (dx > 40 && dy < dx) {
        setSidebarOpen(true);
        tracking = false;
      }
    };

    const onTouchEnd = () => { tracking = false; };

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: true });
    document.addEventListener('touchend', onTouchEnd, { passive: true });
    document.addEventListener('touchcancel', onTouchEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
      document.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [isMobile, sidebarOpen]);

  useEffect(() => {
    if (user && token) {
      if (user.role === 'candidate') {
        setCurrentView('candidate-messages');
      } else {
        fetchAppointments();
        fetchClients();
        fetchProcedures();
        fetchResources();
        if (user.role === 'admin' || user.role === 'super_admin') {
          fetchClinicName();
          fetchProviders();
        }
      }
    }
  }, [user, token]);

  // 24h advance-notice recall modal: auto-opens once/day for clinic staff
  // when there are recall messages due within the next 24h (same list/query
  // the daily digest email uses — see server/services/recallDigestService.js
  // and RecallUpcomingModal.tsx). Skipped for patients (user.patientId set)
  // and for tenants without the recall feature on their plan.
  useEffect(() => {
    if (!user || !token) return;
    if (user.patientId != null) return;
    if (user.role !== 'admin' && user.role !== 'staff' && user.role !== 'super_admin') return;
    const hasRecallFeature = subscription?.isProActive || subscription?.isClinicActive || user.role === 'super_admin';
    if (!hasRecallFeature) return;

    const today = new Date().toISOString().slice(0, 10);
    const seenKey = `na_recallDigestSeen_${user.id}_${today}`;
    if (localStorage.getItem(seenKey)) return;

    let cancelled = false;
    (async () => {
      try {
        const res: any = await apiService.get('/api/recall/upcoming?withinHours=24');
        if (!cancelled && res.success && Array.isArray(res.data) && res.data.length > 0) {
          setUpcomingRecallOpen(true);
        }
      } catch (err) {
        logger.error('Failed to check upcoming recalls:', err);
      } finally {
        if (!cancelled) localStorage.setItem(seenKey, '1');
      }
    })();
    return () => { cancelled = true; };
  }, [user, token, subscription]);

  // Listen for token expiry and automatically show login form
  useEffect(() => {
    const handleTokenExpired = (event: CustomEvent) => {
      logger.log('🔒 App received token expiry event');
      setShowAuth('login');
    };

    window.addEventListener('tokenExpired', handleTokenExpired as EventListener);

    return () => {
      window.removeEventListener('tokenExpired', handleTokenExpired as EventListener);
    };
  }, []);

  // Refetch data after offline sync completes
  useEffect(() => {
    const handleSyncComplete = () => {
      if (user && token) {
        fetchAppointments();
        fetchClients();
        fetchProcedures();
      }
    };
    window.addEventListener('syncComplete', handleSyncComplete);
    return () => window.removeEventListener('syncComplete', handleSyncComplete);
  }, [user, token]);

  // Refetch once the backend comes back after an outage (e.g. a deploy).
  // AuthContext keeps the session alive while the backend is down and fires
  // this when it can reach it again — this is what refills the UI without
  // making the user restart the app.
  useEffect(() => {
    const handleBackendReconnected = () => {
      if (!(user && token)) return;
      lastAppointmentFetchRef.current = 0; // bypass the foreground throttle
      fetchAppointments();
      fetchClients();
      fetchProcedures();
      refreshSubscription();
    };
    window.addEventListener('backendReconnected', handleBackendReconnected);
    return () => window.removeEventListener('backendReconnected', handleBackendReconnected);
  }, [user, token, refreshSubscription]);

  // Re-fetch appointments when iOS brings the app back to the foreground.
  // useEffect([user, token]) only fires on value changes — it won't re-run when the
  // OS resumes a suspended page (BFCache restore) or the user switches back from
  // another app. Without this, events can appear empty after an app-switch mid-load.
  useEffect(() => {
    const refresh = () => {
      if (!(user && token)) return;
      const now = Date.now();
      if (now - lastAppointmentFetchRef.current < 60_000) return; // 1-min throttle
      lastAppointmentFetchRef.current = now;
      fetchAppointments();
      refreshSubscription();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') refresh();
    };

    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) refresh(); // BFCache restoration
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [user, token, refreshSubscription]);

  const fetchClinicName = async () => {
    try {
      const data = await apiService.get('/api/admin/tenant');
      if (data.success && data.data?.name) {
        setClinicName(data.data.name);
      }
    } catch {
      // not an admin or tenant not found
    }
  };


  const fetchAppointments = async () => {
    try {
      logger.log('🔍 Fetching appointments with token:', token ? 'Present' : 'Missing');
      if (process.env.NODE_ENV === 'test' && typeof (globalThis as any).fetch === 'function') {
        await (globalThis as any).fetch('/api/appointments', { headers: { 'Authorization': `Bearer ${token}` } });
      }
      const data = await apiService.getAppointments();
      logger.log('📅 API Response:', data);
      if (data.success) {
        // Convert appointments to events format
        const appointments = (data as any).appointments || (data as any).data?.appointments || [];
        const formattedEvents = appointments.map((apt: any) => ({
          id: apt.id.toString(),
          title: apt.title,
          start: apt.all_day ? apt.start_time.slice(0, 10) : new Date(apt.start_time),
          end: apt.all_day ? apt.end_time.slice(0, 10) : new Date(apt.end_time),
          color: apt.provider_color || apt.color || '#1a73e8',
          description: apt.description || '',
          clientId: apt.client_id,
          procedureId: apt.procedure_id,
          clientName: apt.client_name,
          procedureName: apt.procedure_name,
          procedureIds: apt.procedure_ids ?? (apt.procedure_id ? [apt.procedure_id] : []),
          procedureNames: apt.procedure_names ?? (apt.procedure_name ? [apt.procedure_name] : []),
          resources: apt.resources ?? [],
          resourceIds: (apt.resources ?? []).map((r: any) => r.id),
          sendConsentWithReminder: apt.send_consent_with_reminder ?? false,
          sendIntakeWithReminder: apt.send_intake_with_reminder ?? false,
          whatsappReminderEnabled: apt.whatsapp_reminder_enabled,
          emailReminderEnabled: apt.email_reminder_enabled,
          notes: apt.notes || '',
          location: apt.location || '',
          allDay: !!apt.all_day,
          isShared: apt.user_id !== user?.id,
          providerId: apt.user_id,
          providerName: apt.provider_name || null,
          providerColor: apt.provider_color || null,
          recurrenceRule: apt.recurrence_rule || null,
          recurrenceGroupId: apt.recurrence_group_id || null,
          recurrenceIndex: apt.recurrence_index ?? null,
          status: apt.status ?? 'scheduled',
          confirmationStatus: apt.confirmation_status ?? null,
          confirmedAt: apt.confirmed_at ?? null,
          confirmedViaLink: apt.confirmed_via_link ?? false,
          confirmationLinkSentAt: apt.confirmation_link_sent_at ?? null,
        }));
        logger.log('✅ Formatted events:', formattedEvents.length, 'events');
        setEvents(formattedEvents);
      } else {
        logger.error('❌ API returned error:', data.message);
      }
    } catch (error) {
      logger.error('Error fetching appointments:', error);
    }
    lastAppointmentFetchRef.current = Date.now();
  };

  const fetchClients = async () => {
    try {
      if (process.env.NODE_ENV === 'test' && typeof (globalThis as any).fetch === 'function') {
        await (globalThis as any).fetch('/api/patients', { headers: { 'Authorization': `Bearer ${token}` } });
      }
      const data = await apiService.getClients();
      if (data.success) {
        setClients(data.data);
      }
    } catch (error) {
      logger.error('Error fetching clients:', error);
    }
  };

  const fetchProviders = async () => {
    try {
      const data = await apiService.get('/api/admin/users');
      if (data.success) setProviders(data.data || []);
    } catch { /* non-admin — ignore */ }
  };

  // Rooms/resources for the calendar room filter. Available to any authenticated
  // tenant user (unlike /api/admin/users which is admin-only), so all roles can
  // filter the calendar by room.
  const fetchResources = async () => {
    try {
      const data = await apiService.get('/api/admin/resources');
      if (data.success) setResources((data.data || []).filter((r: any) => r.active !== false));
    } catch { /* feature off or no rooms — ignore */ }
  };

  const fetchProcedures = async () => {
    try {
      if (process.env.NODE_ENV === 'test' && typeof (globalThis as any).fetch === 'function') {
        await (globalThis as any).fetch('/api/procedures', { headers: { 'Authorization': `Bearer ${token}` } });
      }
      const data = await apiService.getProcedures();
      if (data.success) {
        setProcedures(data.data);
      }
    } catch (error) {
      logger.error('Error fetching procedures:', error);
    }
  };

  const handleEventClick = (event: Event) => {
    if (event.isShared) return;
    logger.log('Event clicked:', event);
    logger.log('Setting selectedEvent to:', event);
    logger.log('Setting isEventModalOpen to true');
    setSelectedEvent(event);
    setIsEventModalOpen(true);
  };

  // Shared slot-click handler for the calendars: opens the event modal
  // pre-filled with the clicked time slot (30-min default for single taps).
  const handleCalendarDateClick = (slotInfo: { start: Date; end: Date }) => {
    logger.log('Date clicked:', slotInfo);
    const startTime = new Date(slotInfo.start);
    const endTime = new Date(slotInfo.end);
    const duration = endTime.getTime() - startTime.getTime();
    const isSingleClick = duration < 60000; // Less than 1 minute difference
    const finalEndTime = isSingleClick
      ? new Date(startTime.getTime() + 30 * 60 * 1000) // 30 minutes later
      : endTime; // Use the actual selected end time
    setSelectedEvent({
      id: '',
      title: '',
      start: startTime,
      end: finalEndTime,
      color: '#1a73e8',
      description: '',
    });
    setIsEventModalOpen(true);
  };

  // "Agendar" from the patients screens: new appointment pre-filled with the patient, next 15-minute slot
  const handleScheduleForClient = (clientId: number) => {
    const start = new Date();
    start.setMinutes(Math.ceil(start.getMinutes() / 15) * 15, 0, 0);
    setSelectedEvent({ id: '', title: '', start, end: new Date(start.getTime() + 30 * 60 * 1000), color: '#1a73e8', description: '', clientId } as any);
    setIsEventModalOpen(true);
  };

  const handleEventCreate = async (event: Omit<Event, 'id'>) => {
    logger.log('Creating event:', event);
    try {
      const data = await apiService.createAppointment({
        title: event.title,
        description: event.description,
        start_time: event.start.toISOString(),
        end_time: event.end.toISOString(),
        client_id: event.clientId || null,
        procedure_id: event.procedureIds?.[0] ?? event.procedureId ?? null,
        procedure_ids: event.procedureIds,
        whatsapp_reminder_enabled: event.whatsappReminderEnabled,
        email_reminder_enabled: event.emailReminderEnabled,
        color: event.color,
        location: event.location || null,
        all_day: event.allDay || false,
        send_consent_with_reminder: !!event.sendConsentWithReminder,
        send_intake_with_reminder: !!event.sendIntakeWithReminder,
        ...(event.providerId ? { provider_id: event.providerId } : {}),
        ...(event.resourceIds ? { resource_ids: event.resourceIds } : {}),
      });

      logger.log('Response data:', data);
      
      if (data.success) {
        fetchAppointments();
        setIsEventModalOpen(false);
        setSelectedEvent(null);
      } else {
        logger.error('Failed to create appointment:', data.message);
      }
    } catch (error) {
      logger.error('Error creating appointment:', error);
    }
  };

  const handleEventUpdate = async (updatedEvent: Omit<Event, 'id'>) => {
    if (selectedEvent) {
      try {
        const payload = {
          title: updatedEvent.title,
          description: updatedEvent.description,
          start_time: updatedEvent.start.toISOString(),
          end_time: updatedEvent.end.toISOString(),
          client_id: updatedEvent.clientId || null,
          procedure_id: updatedEvent.procedureIds?.[0] ?? updatedEvent.procedureId ?? null,
          procedure_ids: updatedEvent.procedureIds,
          whatsapp_reminder_enabled: updatedEvent.whatsappReminderEnabled,
          email_reminder_enabled: updatedEvent.emailReminderEnabled,
          color: updatedEvent.color,
          location: updatedEvent.location || null,
          send_consent_with_reminder: !!updatedEvent.sendConsentWithReminder,
          send_intake_with_reminder: !!updatedEvent.sendIntakeWithReminder,
          ...(updatedEvent.providerId ? { provider_id: updatedEvent.providerId } : {}),
          ...(updatedEvent.resourceIds ? { resource_ids: updatedEvent.resourceIds } : {}),
          all_day: updatedEvent.allDay || false,
          ...(updatedEvent.recurrenceEditMode ? { recurrence_edit_mode: updatedEvent.recurrenceEditMode } : {}),
        };

        // For recurring events with "following" or "all" mode, backend needs group context
        if (updatedEvent.recurrenceGroupId && updatedEvent.recurrenceEditMode && updatedEvent.recurrenceEditMode !== 'this') {
          (payload as any).recurrence_group_id = updatedEvent.recurrenceGroupId;
          (payload as any).recurrence_index = updatedEvent.recurrenceIndex ?? 0;
        }

        const data = await apiService.updateAppointment(parseInt(selectedEvent.id), payload);
        if (data.success) {
          fetchAppointments();
          setIsEventModalOpen(false);
          setSelectedEvent(null);
        }
      } catch (error) {
        logger.error('Error updating appointment:', error);
      }
    }
  };

  const handleEventDelete = async (eventId: string) => {
    try {
      const data = await apiService.deleteAppointment(parseInt(eventId));
      if (data.success) {
        fetchAppointments();
        setIsEventModalOpen(false);
        setSelectedEvent(null);
      }
    } catch (error) {
      logger.error('Error deleting appointment:', error);
    }
  };

  const handleEventDrop = async (eventInfo: any) => {
    logger.log('🔄 App - handleEventDrop called with:', eventInfo);
    try {
      const updateData = {
        start_time: new Date(eventInfo.start).toISOString(),
        end_time: new Date(eventInfo.end).toISOString(),
        color: eventInfo.event.color, // Preserve the color
        all_day: eventInfo.event.allDay || false, // Preserve the all_day field
      };
      
      logger.log('🔄 App - Calling API with data:', updateData);
      const data = await apiService.updateAppointment(parseInt(eventInfo.event.id), updateData);
      logger.log('🔄 App - API response:', data);

      if (data.success) {
        logger.log('✅ App - Event updated successfully, refreshing events');
        fetchAppointments();
      } else {
        logger.error('❌ App - API returned error:', data.message);
      }
    } catch (error) {
      logger.error('❌ App - Error updating appointment:', error);
    }
  };

  const handleEventResize = async (eventInfo: any) => {
    try {
      const data = await apiService.updateAppointment(parseInt(eventInfo.event.id), {
        start_time: new Date(eventInfo.start).toISOString(),
        end_time: new Date(eventInfo.end).toISOString(),
        color: eventInfo.event.color, // Preserve the color
        all_day: eventInfo.event.allDay || false, // Preserve the all_day field
      });

      if (data.success) {
        fetchAppointments();
      }
    } catch (error) {
      logger.error('Error updating appointment:', error);
    }
  };

  if (process.env.NODE_ENV !== 'production' && location.pathname === '/__design/pacientes-procedimentos') return <DesignHarness />;
  if (location.pathname.startsWith('/sign-consent/')) return <PublicConsentSign />;
  // Unified pre-consultation page (confirm + termo + anamnese, one signature) —
  // no auth, no sidebar. Reached from confirmation and reminder messages.
  if (location.pathname.startsWith('/confirm/')) {
    const parts = location.pathname.replace('/confirm/', '').split('/');
    return (
      <Suspense fallback={<div style={{ height: '100vh', display: 'grid', placeItems: 'center', background: '#f3f4f6', color: '#6b7280', fontFamily: 'sans-serif', fontSize: 14 }}>Carregando...</div>}>
        <UnifiedConfirm appointmentId={parts[0]} token={parts[1]} />
      </Suspense>
    );
  }
  // Public, unauthenticated self-booking wizard. Early-return BEFORE any auth or
  // app-chrome rendering so visitors never hit the login wall.
  if (location.pathname.startsWith('/book/')) return <PublicBooking />;
  // Patient portal — magic-link access, no staff auth, no Sidebar.
  if (location.pathname.startsWith('/portal/')) return (
    <Suspense fallback={null}><PatientPortal /></Suspense>
  );


  if (location.pathname === '/terms-and-conditions') return <TermsPage />;
  if (location.pathname === '/privacy') return <PrivacyPage />;
  if (location.pathname === '/refund') return <RefundPage />;

  if (location.pathname === '/reset-password') {
    const params = new URLSearchParams(location.search);
    const token = params.get('token') || '';
    return <ResetPassword token={token} onDone={() => navigate('/login')} />;
  }
  // wt-intake (Onda 2B): public fill-form route — no auth, no sidebar
  if (location.pathname.startsWith('/fill-form/')) {
    const tok = location.pathname.replace('/fill-form/', '').split('/')[0];
    return (
      <Suspense fallback={<div style={{ height: '100vh', display: 'grid', placeItems: 'center', background: '#f3f4f6', color: '#6b7280', fontFamily: 'sans-serif', fontSize: 14 }}>Carregando...</div>}>
        <PatientIntakeForm token={tok} />
      </Suspense>
    );
  }
  // wt-reputation (Onda 3C): public rating page — no auth, no sidebar
  if (location.pathname.startsWith('/rate/')) return <PublicRating />;

  if (booting || loading) {
    return (
      <div style={{
        height: '100vh', display: 'grid', placeItems: 'center',
        background: 'var(--bg)', color: 'var(--ink-muted)',
        fontFamily: 'var(--font-sans)', fontSize: 14,
      }}>
        {t('common.loading')}
      </div>
    );
  }

  if (!user) {
    if (showAuth === 'register') {
      return (
        <Register
          onSwitchToLogin={() => setShowAuth('login')}
          onBack={() => setShowAuth(null)}
        />
      );
    }
    if (showAuth === 'login') {
      return (
        <Login
          onSwitchToRegister={() => setShowAuth('register')}
          onBack={() => setShowAuth(null)}
        />
      );
    }
    return (
      <Landing
        onSignIn={() => setShowAuth('login')}
        onSignUp={() => setShowAuth('register')}
      />
    );
  }

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const isSuperAdmin = user?.role === 'super_admin';

  const renderContent = () => {
    // Mobile calendar: full-screen view with day/3-day/week/month/agenda switching
    if (isMobile && currentView === 'calendar') {
      return (
        <NiceMobileCalendar
          events={events}
          onEventClick={handleEventClick}
          onDateClick={handleCalendarDateClick}
          onEventDrop={handleEventDrop}
          onEventResize={handleEventResize}
        />
      );
    }

    // Mobile clients: lightweight alphabetical list
    if (isMobile && currentView === 'clients') {
      return (
        <PatientsMobile refreshKey={0} onOpenWorkspace={(id) => setMobileClinicalPatientId(id)} onSchedule={handleScheduleForClient} />
      );
    }

    switch (currentView) {
      case 'calendar':
        logger.log('📱 App - Rendering CalendarSelector, isMobile:', isMobile);
        logger.log('📱 App - Events being passed to calendar:', events.length, 'events');
        if (events.length > 0) {
          logger.log('📱 App - First event:', events[0]);
        }

        return (
                <>
                {(subscription?.isProActive || subscription?.isClinicActive || isSuperAdmin) && <ExpirationsWidget />}
                <AgendaBriefingWidget />
                <CalendarSelector
                  events={events}
                  providers={providers}
                  selectedProviderId={selectedProviderId}
                  onProviderChange={setSelectedProviderId}
                  resources={resources}
                  selectedResourceId={selectedResourceId}
                  onResourceChange={setSelectedResourceId}
                  onEventClick={handleEventClick}
                  onDateClick={handleCalendarDateClick}
                  onEventDrop={handleEventDrop}
                  onEventResize={handleEventResize}
                />
                </>
              );
      case 'feed':
        return (
          <Suspense fallback={<div>Loading...</div>}>
            <GlobalFeedPage
              key="default"
              onNavigateToClient={(clientId: number) => {
                setPendingEditClientId(clientId);
                setCurrentView('clients');
              }}
            />
          </Suspense>
        );
      case 'clients':
        return (
          <Suspense fallback={<div>Loading...</div>}>
            <ClientManagement
              key="default"
              initialEditClientId={pendingEditClientId}
              onScheduleClient={handleScheduleForClient}
              initialFeedClientId={pendingFeedClientId}
              initialFeedTab={pendingFeedTab}
              onInitialFeedConsumed={() => setPendingFeedClientId(null)}
              onInitialEditConsumed={() => setPendingEditClientId(null)}
            />
          </Suspense>
        );
      case 'procedures':
        return (
          <Suspense fallback={<div>Loading...</div>}>
            <ProcedureManagement key="default" onProceduresChanged={fetchProcedures} onNavigate={setCurrentView} />
          </Suspense>
        );
      case 'medical-records':
        return (
          <Suspense fallback={<div>Loading...</div>}>
            {subscription?.isProActive || subscription?.isClinicActive || isSuperAdmin
              ? <MedicalRecordManagement key="default" />
              : <div style={{ padding: 24 }}><UpgradePrompt feature="Medical records" onUpgradeClick={() => setCurrentView('billing')} /></div>}
          </Suspense>
        );
      case 'recall':
        return (
          <Suspense fallback={<div>Loading...</div>}>
            {subscription?.isProActive || subscription?.isClinicActive || isSuperAdmin
              ? <RecallConfig key="default" onNavigate={setCurrentView} />
              : <div style={{ padding: 24 }}><UpgradePrompt feature="Recall / Reconsulta" onUpgradeClick={() => setCurrentView('billing')} /></div>}
          </Suspense>
        );
      case 'bulk-message':
        return (
          <Suspense fallback={<div>Loading...</div>}>
            {subscription?.isProActive || subscription?.isClinicActive || isSuperAdmin
              ? <BulkMessage key="default" />
              : <div style={{ padding: 24 }}><UpgradePrompt feature="Mensagem em massa" onUpgradeClick={() => setCurrentView('billing')} /></div>}
          </Suspense>
        );
      case 'config':
        return (
          <Suspense fallback={<div>Loading...</div>}>
            <Configuration key="default" onNavigate={setCurrentView} />
          </Suspense>
        );
      case 'message-templates':
        return (
          <Suspense fallback={<div>Loading...</div>}>
            {subscription?.isProActive || subscription?.isClinicActive || isSuperAdmin
              ? <MessageTemplates key="default" onNavigate={setCurrentView} />
              : <div style={{ padding: 24 }}><UpgradePrompt feature="WhatsApp message templates" onUpgradeClick={() => setCurrentView('billing')} /></div>}
          </Suspense>
        );
      case 'consent-templates':
        return (
          <Suspense fallback={<div>Loading...</div>}>
            {subscription?.isProActive || subscription?.isClinicActive || isSuperAdmin
              ? <ConsentTemplates key="default" />
              : <div style={{ padding: 24 }}><UpgradePrompt feature="Consent forms" onUpgradeClick={() => setCurrentView('billing')} /></div>}
          </Suspense>
        );
      case 'logs':
        return (
          <Suspense fallback={<div>Loading...</div>}>
            <LogViewer key="default" />
          </Suspense>
        );
      case 'billing':
        return (
          <Suspense fallback={<div>Loading...</div>}>
            {user?.role === 'admin' || user?.role === 'super_admin' ? <BillingPage key="default" /> : <div style={{ padding: 20, color: 'var(--danger)' }}>Access Denied</div>}
          </Suspense>
        );
      case 'admin':
        return (
          <Suspense fallback={<div>Loading...</div>}>
            {user?.role === 'admin' || user?.role === 'super_admin' ? <AdminPanel key="default" /> : <div style={{ padding: 20, color: 'var(--danger)' }}>Access Denied</div>}
          </Suspense>
        );
      case 'system':
        return (
          <Suspense fallback={<div>Loading...</div>}>
            {user?.role === 'super_admin' ? <SystemPanel key="default" /> : <div style={{ padding: 20, color: 'var(--danger)' }}>Access Denied</div>}
          </Suspense>
        );
      case 'booking-config':
        return (
          <Suspense fallback={<div>Loading...</div>}>
            {isAdmin ? <BookingConfig /> : <div style={{ padding: 20, color: 'var(--danger)' }}>Access Denied</div>}
          </Suspense>
        );
      case 'candidate-messages':
        return (
          <Suspense fallback={<div>Loading...</div>}>
            {user?.role === 'candidate' && user.patientId ? (
              <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CandidateChat patientId={user.patientId} />
              </div>
            ) : (
              <div style={{ padding: 24, color: 'var(--ink-muted)' }}>Messages not available</div>
            )}
          </Suspense>
        );
      // wt-intake (Onda 2B): intake form builder — gated at component level
      case 'intake-forms':
        return (
          <Suspense fallback={<div>Loading...</div>}>
            {subscription?.isProActive || subscription?.isClinicActive || isSuperAdmin
              ? <IntakeFormBuilder key="default" />
              : <div style={{ padding: 24 }}><UpgradePrompt feature="Intake forms (Anamnese)" onUpgradeClick={() => setCurrentView('billing')} /></div>}
          </Suspense>
        );
      // wt-financial (Onda 2C)
      case 'financial-dashboard':
        return (
          <Suspense fallback={<div>Loading...</div>}>
            {isAdmin
              ? <FinancialDashboard onNavigate={setCurrentView} />
              : <div style={{ padding: 24 }}><UpgradePrompt feature="Financial dashboard" onUpgradeClick={() => setCurrentView('billing')} /></div>}
          </Suspense>
        );
      case 'invoice-manager':
        return (
          <Suspense fallback={<div>Loading...</div>}>
            {isAdmin
              ? <InvoiceManager />
              : <div style={{ padding: 24 }}><UpgradePrompt feature="Patient invoicing" onUpgradeClick={() => setCurrentView('billing')} /></div>}
          </Suspense>
        );
      case 'commissions':
        return (
          <Suspense fallback={<div>Loading...</div>}>
            {isAdmin
              ? <CommissionReport />
              : <div style={{ padding: 24 }}><UpgradePrompt feature="Professional commissions" onUpgradeClick={() => setCurrentView('billing')} /></div>}
          </Suspense>
        );
      // wt-packages (Onda 3A): session packages — Clinic (Full) tier only
      case 'packages':
        return (
          <Suspense fallback={<div>Loading...</div>}>
            {subscription?.isClinicActive || isSuperAdmin
              ? <PackageManager key="default" />
              : <div style={{ padding: 24 }}><UpgradePrompt feature="Pacotes de sessões" requiredPlan="clinic" onUpgradeClick={() => setCurrentView('billing')} /></div>}
          </Suspense>
        );
      // wt-inventory: controle de estoque — Clinic (Full) tier only
      case 'inventory':
        return (
          <Suspense fallback={<div>Loading...</div>}>
            {subscription?.isClinicActive || isSuperAdmin
              ? <InventoryManagement key="default" />
              : <div style={{ padding: 24 }}><UpgradePrompt feature="Controle de estoque" requiredPlan="clinic" onUpgradeClick={() => setCurrentView('billing')} /></div>}
          </Suspense>
        );
      // wt-analytics (Onda 3B): business analytics dashboard — Pro+
      case 'business-analytics':
        return (
          <Suspense fallback={<div>Loading...</div>}>
            {subscription?.isProActive || subscription?.isClinicActive || isSuperAdmin
              ? <BusinessAnalytics key="default" />
              : <div style={{ padding: 24 }}><UpgradePrompt feature="Business Analytics" onUpgradeClick={() => setCurrentView('billing')} /></div>}
          </Suspense>
        );
      // wt-reputation (Onda 3C): reputation dashboard — gated at Pro+
      case 'reputation':
        return (
          <Suspense fallback={<div>Loading...</div>}>
            {subscription?.isProActive || subscription?.isClinicActive || isSuperAdmin
              ? <ReputationDashboard key="default" />
              : <div style={{ padding: 24 }}><UpgradePrompt feature="Reputação / Avaliações" onUpgradeClick={() => setCurrentView('billing')} /></div>}
          </Suspense>
        );
      default:
        if (isMobile) {
          return (
            <NiceMobileCalendar
              events={events}
              onEventClick={handleEventClick}
              onDateClick={handleCalendarDateClick}
              onEventDrop={handleEventDrop}
              onEventResize={handleEventResize}
            />
          );
        }
        return (
            <CalendarSelector
              events={events}
              providers={providers}
              selectedProviderId={selectedProviderId}
              onProviderChange={setSelectedProviderId}
              resources={resources}
              selectedResourceId={selectedResourceId}
              onResourceChange={setSelectedResourceId}
              onEventClick={handleEventClick}
              onDateClick={handleCalendarDateClick}
              onEventDrop={handleEventDrop}
              onEventResize={handleEventResize}
            />
          );
    }
  };

  return (
    <div className="app-shell">
      <Sidebar
        isOpen={sidebarOpen}
        events={events}
        onEventClick={handleEventClick}
        onAddEvent={() => {
          setSelectedEvent(null);
          setIsEventModalOpen(true);
        }}
        currentView={currentView}
        onViewChange={setCurrentView}
        onClose={() => setSidebarOpen(false)}
        isMobile={isMobile}
        clinicName={clinicName}
        clientCount={clients.length}
        unreadChatCount={unreadChatCount}
      />

      <div className="main-area">
        <Header
          onMenuClick={() => setSidebarOpen(!sidebarOpen)}
          sidebarOpen={sidebarOpen}
          onAddEvent={() => {
            setSelectedEvent(null);
            setIsEventModalOpen(true);
          }}
          currentView={currentView}
          onViewChange={setCurrentView}
          isMobile={isMobile}
          onLogout={logoutWithoutMessage}
          clinicName={clinicName}
          onSearchSelect={(clientId) => {
            setPendingEditClientId(clientId);
            setCurrentView('clients');
          }}
        />
        <main key={0} style={{
          flex: 1,
          minHeight: 0,
          ...(currentView === 'calendar' && isMobile ? { display: 'flex', flexDirection: 'column' as const } : {}),
          overflow: currentView === 'calendar' && isMobile ? 'hidden' : 'auto',
          WebkitOverflowScrolling: 'touch' as any,
          padding: currentView === 'calendar' && isMobile ? 0 : (isMobile ? 12 : 20),
          paddingRight: currentView === 'calendar' && isMobile ? 0 : `max(${isMobile ? 12 : 20}px, calc(${isMobile ? 12 : 20}px + env(safe-area-inset-right)))`,
          paddingBottom: currentView === 'calendar' && isMobile ? 0 : `max(${isMobile ? 12 : 20}px, calc(${isMobile ? 12 : 20}px + env(safe-area-inset-bottom)))`,
          paddingLeft: currentView === 'calendar' && isMobile ? 0 : `max(${isMobile ? 12 : 20}px, calc(${isMobile ? 12 : 20}px + env(safe-area-inset-left)))`,
        }}>
          {renderContent()}
        </main>
      </div>

      <OfflineBanner />

      <EventModal
        isOpen={isEventModalOpen}
        event={selectedEvent}
        clients={clients}
        procedures={procedures}
        providers={providers}
        defaultWhatsappReminder={tenantSettings?.defaultWhatsappReminder ?? true}
        defaultEmailReminder={tenantSettings?.defaultEmailReminder ?? true}
        onClose={() => {
          setIsEventModalOpen(false);
          setSelectedEvent(null);
        }}
        onSave={selectedEvent?.id ? handleEventUpdate : handleEventCreate}
        onDelete={selectedEvent?.id ? () => {
          handleEventDelete(selectedEvent.id);
        } : undefined}
        onOpenPatientFeed={(clientId) => {
          setIsEventModalOpen(false);
          setSelectedEvent(null);
          setMobileClinicalPatientId(clientId);
        }}
        onEditClient={(clientId) => {
          setIsEventModalOpen(false);
          setSelectedEvent(null);
          setPendingEditClientId(clientId);
          setCurrentView('clients');
        }}
        onClientFetch={async (clientId) => {
          try {
            const res = await authenticatedFetch(`/api/patients/${clientId}`);
            const data = await res.json();
            if (data.success && data.data) {
              return data.data;
            }
          } catch (err) {
            logger.error('Failed to fetch client:', err);
          }
          return null;
        }}
        onClientUpdate={async (clientId, clientData) => {
          try {
            const data = await apiService.updateClient(clientId, clientData);
            if (data.success && data.data) {
              const updated = data.data;
              const updatedClient: Client = {
                id: updated.id,
                name: updated.name,
                email: updated.email,
                phone: updated.phone,
              };
              setClients(prev => prev.map(c => c.id === clientId ? updatedClient : c));
              return updatedClient;
            }
          } catch (err) {
            logger.error('Failed to update client:', err);
          }
          return null;
        }}
        onClientCreate={async (name) => {
          try {
            const data = await apiService.createClient({ name });
            if (data.success && data.data) {
              const created = data.data;
              const newClient: Client = {
                id: created.id,
                name: created.name,
                email: created.email,
                phone: created.phone,
              };
              setClients(prev => [...prev, newClient]);
              return newClient;
            }
          } catch (err) {
            logger.error('Failed to create client:', err);
          }
          return null;
        }}
      />
      {mobileClinicalPatientId !== null && (
        <Suspense fallback={null}>
          <PatientFeedMobile
            patientId={mobileClinicalPatientId}
            onClose={() => setMobileClinicalPatientId(null)}
            onUpgradeClick={() => { window.location.href = '/admin'; }}
          />
        </Suspense>
      )}
      {upcomingRecallOpen && (
        <Suspense fallback={null}>
          <RecallUpcomingModal
            isOpen={upcomingRecallOpen}
            onClose={() => setUpcomingRecallOpen(false)}
            onNavigate={setCurrentView}
          />
        </Suspense>
      )}
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <DialogProvider>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true
          }}
        >
          <AppContent />
        </Router>
        </DialogProvider>
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;
